import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import routesfe from '../Config/RoutesFE';
import { pricingPlans, getSavePercentage } from '../Config/PricingPlans';
import PlanCard from '../components copy/PlanCard';
import ContactSalesModal from '../components copy/ContactSalesModal';
import configDetails from '../Config/Config';
import PaymentService from '../services/PaymentService';
import logger from '../utils/Logger';
import Toast from '../components copy/Toast';

const COMPONENT_NAME = 'PlansPage';

const PLAN_ORDER = ['free trial', 'basic', 'professional', 'enterprise'];

const PlansPage = () => {
  // Find the index of the Professional plan (or fallback to 0)
  const professionalIndex = pricingPlans.findIndex(plan => plan.name.toLowerCase() === 'professional' && plan.billingCycle === 'MONTHLY');
  const [hoveredPlan, setHoveredPlan] = useState(professionalIndex !== -1 ? professionalIndex : 0);
  const navigate = useNavigate();
  const [filteredPlans, setFilteredPlans] = useState(null);
  const [currentPlanName, setCurrentPlanName] = useState('');
  const [currentPlanCode, setCurrentPlanCode] = useState('');
  const [currentPlanType, setCurrentPlanType] = useState('MONTHLY');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [authIdToken, setAuthIdToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('MONTHLY');
  const [cognitoIsSubscribed, setCognitoIsSubscribed] = useState(false);
  const [cognitoIsTrialUser, setCognitoIsTrialUser] = useState(false);
  const [isActivatingTrial, setIsActivatingTrial] = useState(false);
  const [toast, setToast] = useState(null);

  const handleSignOut = async () => {
    try {
      await Auth.signOut();
      sessionStorage.removeItem('noteforge_trial_activated');
      sessionStorage.removeItem('noteforge_selected_billing_cycle');
      navigate(routesfe.LANDING);
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Load selected billing cycle from sessionStorage on mount
  useEffect(() => {
    const savedCycle = sessionStorage.getItem('noteforge_selected_billing_cycle');
    if (savedCycle === 'YEARLY' || savedCycle === 'MONTHLY') {
      setSelectedBillingCycle(savedCycle);
    }
  }, []);
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        setIsAuthenticated(!!user);
        const idToken = user?.signInUserSession?.idToken?.jwtToken;
        setAuthIdToken(idToken);
      } catch {
        setIsAuthenticated(false);
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Fetch current plan and filter plans
  useEffect(() => {
    const fetchAndFilter = async () => {
      setLoading(true);
      try {
        if (!isAuthenticated) {
          // Filter by selected billing cycle (include plans without billingCycle)
          const filtered = pricingPlans.filter(plan => !plan.billingCycle || plan.billingCycle === selectedBillingCycle);
          setFilteredPlans(filtered);
          setLoading(false);
          return;
        }

        const storedUser = JSON.parse(localStorage.getItem('user'));
        const id = storedUser?.cognito_user_id || storedUser?.userSub;
        if (!id || !authIdToken) {
          const filtered = pricingPlans.filter(plan => !plan.billingCycle || plan.billingCycle === selectedBillingCycle);
          setFilteredPlans(filtered);
          setLoading(false);
          return;
        }

        // Fetch Cognito custom attributes to determine true subscription/trial status
        let attrsIsSubscribed = false;
        let attrsIsTrialUser = false;
        try {
          const currentCognitoUser = await Auth.currentAuthenticatedUser();
          const attrs = currentCognitoUser?.attributes || {};
          attrsIsSubscribed = attrs['custom:is_subscribed'] === '1';
          attrsIsTrialUser = attrs['custom:is_trial_user'] === '1';
        } catch (attrErr) {
          console.error('[PlansPage] Error getting Cognito attributes:', attrErr);
        }
        setCognitoIsSubscribed(attrsIsSubscribed);
        setCognitoIsTrialUser(attrsIsTrialUser);

        const { trackingUrl, GET_TRACKING_TIME } = configDetails;
        const res = await fetch(`${trackingUrl}${GET_TRACKING_TIME}?user_id=${id}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authIdToken
          }
        });

        if (!res.ok) throw new Error('Failed to fetch tracking time');
        const data = await res.json();
        const rawPlanName = (data.subscription_status_detail?.plan_name || '').toLowerCase();
        const planCode = (data.subscription_status_detail?.plan_code || '').toUpperCase();
        const planId = data.subscription_status_detail?.plan_id;
        const subscriptionStatus = (data.subscription_status_detail?.status || '').toLowerCase();
        let planType = (data.subscription_status_detail?.plan_type || '').toUpperCase();
        
        // Normalize planType: ANNUAL → YEARLY to match PricingPlans.js
        if (planType === 'ANNUAL') {
          planType = 'YEARLY';
        }
        // Derive plan type from plan_name, plan_code, or plan_id if plan_type is missing
        if (!planType) {
          if (/annual|yearly/i.test(rawPlanName) || planCode.includes('-ANN')) {
            planType = 'YEARLY';
          } else if (planId) {
            const matchedPlan = pricingPlans.find(p => p.id === planId || p.pricingId === planId);
            planType = matchedPlan?.billingCycle || 'MONTHLY';
          } else {
            planType = 'MONTHLY';
          }
        }
        // Extract base plan name (remove "annual"/"yearly"/"monthly" suffix)
        const planName = rawPlanName.replace(/\s*(annual|yearly|monthly)\s*/gi, '').trim();
        setSubscriptionStatus(subscriptionStatus);
        setCurrentPlanName(planName);
        setCurrentPlanCode(planCode);
        setCurrentPlanType(planType);

       // Filter by selected billing cycle (include plans without billingCycle)
        const filtered = pricingPlans.filter(plan => !plan.billingCycle || plan.billingCycle === selectedBillingCycle);
        setFilteredPlans(filtered);
      } catch (error) {
        // If there's an error fetching, show all plans
        console.error('Error fetching current plan:', error);
        const filtered = pricingPlans.filter(plan => !plan.billingCycle || plan.billingCycle === selectedBillingCycle);
        setFilteredPlans(filtered);
      }
      setLoading(false);
    };
    fetchAndFilter();
  }, [isAuthenticated, authIdToken, selectedBillingCycle]);

  // Subscribe button logic (same as LandingPage)
  const handleSubscribe = async (planName) => {
    if (planName === 'Free Trial') {
      if (isActivatingTrial) return;
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        const cognitoUserId = currentUser?.attributes?.sub || currentUser?.username;
        
        if (cognitoUserId) {
          setIsActivatingTrial(true);
          const trialUrl = `${configDetails.apiBaseUrlOrderservice}${configDetails.activateTrialEndpoint}`;
          try {
            const trialRes = await fetch(trialUrl, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ cognitoUserId })
            });
            if (!trialRes.ok) {
              const errText = await trialRes.text();
              setIsActivatingTrial(false);
              setToast({ message: 'Trial activation failed. Please try again later.', type: 'error' });
              return;
            }
            try {
              await Auth.updateUserAttributes(currentUser, {
                'custom:is_trial_user': '1'
              });
            } catch (attrErr) {
              logger.error('Free Trial - Failed to update Cognito attribute', {
                component: COMPONENT_NAME,
                action: 'FREE_TRIAL_ATTRIBUTE_UPDATE_FAILED',
                error: attrErr,
                context: { cognitoUserId }
              });
            }
            // Set sessionStorage flag so RequireAuth can recognize trial user across route changes
            sessionStorage.setItem('noteforge_trial_activated', 'true');
            setToast({ message: 'Free trial activated successfully! 🎉', type: 'success' });
            setTimeout(() => {
              navigate(routesfe.HOME, { state: { trialJustActivated: true } });
            }, 1500);
            return;
          } catch (fetchErr) {
            setIsActivatingTrial(false);
            setToast({ message: 'Trial activation failed. Please try again later.', type: 'error' });
            return;
          }
        } else {
          logger.warn('Free Trial - Missing cognitoUserId', {
            component: COMPONENT_NAME,
            action: 'FREE_TRIAL_MISSING_USER_ID'
          });
          navigate(routesfe.SIGNUP);
          return;
        }
      } catch (err) {
        logger.error('Free Trial - Failed to activate', {
          component: COMPONENT_NAME,
          action: 'FREE_TRIAL_ACTIVATION_FAILED',
          error: err
        });
        navigate(routesfe.SIGNUP);
        return;
      }
      return;
    }

    // For Enterprise, don't require billingCycle match since it appears in both
    let selectedPlan;
    if (planName === 'Enterprise') {
      selectedPlan = pricingPlans.find(plan => plan.name === planName);
      if (selectedPlan) {
        setShowContactModal(true);
        return;
      }
    }
    
    selectedPlan = pricingPlans.find(plan => plan.name === planName && (plan.billingCycle === selectedBillingCycle || !plan.billingCycle));
    if (!selectedPlan) {
      console.error('Plan not found:', planName);
      return;
    }
    // If Enterprise is selected, open Contact Sales modal instead of buy flow
    if (selectedPlan.name === 'Enterprise') {
      setShowContactModal(true);
      return;
    }

    if (!isAuthenticated) {
      // Redirect to login, pass intended plan for payment
      navigate(routesfe.LOGIN, {
        state: {
          redirect: routesfe.PAYMENT,
          selectedPlan: selectedPlan,
          billingCycle: selectedBillingCycle
        }
      });
    } else {
      // Go directly to payment page with complete plan data
      navigate(routesfe.PAYMENT, {
        state: {
          selectedPlan: selectedPlan,
          billingCycle: selectedBillingCycle
        }
      });
    }
  };

  // Calculate savings percentage dynamically for the Professional plan
  const getAnnualSavingsPercentage = () => {
    const monthlyProfessional = pricingPlans.find(p => p.name === 'Professional' && p.billingCycle === 'MONTHLY');
    const yearlyProfessional = pricingPlans.find(p => p.name === 'Professional' && p.billingCycle === 'YEARLY');
    
    if (monthlyProfessional && yearlyProfessional) {
      return getSavePercentage(yearlyProfessional.price, monthlyProfessional.price);
    }
    return '';
  };

  const [showContactModal, setShowContactModal] = useState(false);

  // Use filteredPlans (filtered based on current plan)
  const totalPlans = filteredPlans?.length ?? 0;

  if (loading && isAuthenticated) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center">
        <div className="w-16 h-16 mb-6 border-4 border-gray-200 border-t-purple-600 rounded-full animate-spin"></div>
        
        <h2 className="text-2xl font-semibold text-gray-800">
          Loading Plans
        </h2>
        
        <p className="text-gray-500 text-base mt-2 text-center">
          Please wait while we prepare your available plans.
        </p>
      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-gray-50">
      <Toast toast={toast} onClose={() => setToast(null)} />
      <header className="py-12 text-center">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-3">
          Choose the plan that's right for you
        </h1>
        <p className="text-lg text-gray-500">
          {isAuthenticated && currentPlanName
            ? `You are currently on the ${currentPlanName.charAt(0).toUpperCase() + currentPlanName.slice(1)} plan`
            : 'Flexible pricing for every stage of your journey'}
        </p>
        {/* Monthly/Yearly Toggle */}
        <div className="mt-8 flex items-center justify-center">
          <div className="inline-flex rounded-full bg-gray-100 p-1 relative">
            {/* Monthly button */}
            <button
              onClick={() => {
                setSelectedBillingCycle('MONTHLY');
                sessionStorage.setItem('noteforge_selected_billing_cycle', 'MONTHLY');
              }}
              className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap ${
                selectedBillingCycle === 'MONTHLY'
                  ? 'bg-white text-gray-800 shadow-md'
                  : 'bg-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Monthly
            </button>
            
            {/* Annual button with badge */}
            <button
              onClick={() => {
                setSelectedBillingCycle('YEARLY');
                sessionStorage.setItem('noteforge_selected_billing_cycle', 'YEARLY');
              }}
              className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap flex items-center gap-2 ${
                selectedBillingCycle === 'YEARLY'
                  ? 'bg-white text-gray-800 shadow-md'
                  : 'bg-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Annual
              <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                save {getAnnualSavingsPercentage()}
              </span>
            </button>
          </div>
        </div>
        {isAuthenticated && (
          <div className="absolute top-4 right-8 flex items-center gap-3">
            {cognitoIsSubscribed || cognitoIsTrialUser ? (
              /* Return to Home for users who have access */
              <button
                className="bg-gradient-to-r from-purple-600 via-indigo-600 to-pink-500 text-white px-3 py-2 rounded-2xl shadow-lg font-semibold text-base transition-all duration-200 hover:scale-105 hover:shadow-2xl hover:from-purple-700 hover:via-indigo-700 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-400 min-w-[110px]"
                onClick={() => navigate(routesfe.HOME)}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4"/></svg>
                  Return to Home
                </span>
              </button>
            ) : (
              /* Show Sign Out for new users who don't have access to home */
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-red-600 font-medium text-sm transition-all duration-200 hover:scale-105"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                Sign Out
              </button>
            )}
          </div>
        )}
      </header>
      <main className="pb-16 px-4">
        <div className="container mx-auto">
          <div className={`grid gap-6 mx-auto justify-center ${totalPlans >= 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 max-w-7xl' : totalPlans >= 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl' : totalPlans === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-2xl' : 'grid-cols-1 max-w-sm'}`}>
            {filteredPlans && filteredPlans.map((plan, index) => {
              // Tier ranking based on plan names (not plan codes, since API returns empty planCode)
              const PLAN_TIERS = { 'free trial': 0, 'basic': 1, 'professional': 2, 'enterprise': 3 };
              const CODE_TO_TIER = { 'FREETRIAL': 0, 'BAS': 1, 'BAS-ANN': 1, 'PRO': 2, 'PRO-ANN': 2, 'ENT': 3 };
              
              // Determine current plan tier
              const currentPlanTier = PLAN_TIERS[currentPlanName] || CODE_TO_TIER[currentPlanCode] || 0;
              const planTier = PLAN_TIERS[plan.name.toLowerCase()] || 0;

              const planNameLower = plan.name.toLowerCase();
              const planCodeUpper = (plan.planCode || '').toUpperCase();
              const planBaseCode = planCodeUpper.replace(/-ANN$/, '');
              const currentBaseCode = currentPlanCode.replace(/-ANN$/, '');
              // Special handling for Free Trial (no billingCycle)
              // Free Trial card should always be clickable to activate trial, even if API returns it as current plan
              const isFreeTrialCurrentPlan = planNameLower === 'free trial' && currentPlanName === 'free trial';
              const isCurrentPlan = !isFreeTrialCurrentPlan && ((planNameLower === currentPlanName || planCodeUpper === currentPlanCode || planBaseCode === currentBaseCode) && plan.billingCycle === currentPlanType);
              // Check if this is a downgrade (lower tier within the same billing cycle)
              const isDowngrade = currentPlanName && !isCurrentPlan && planTier < currentPlanTier && plan.billingCycle === currentPlanType;
              // Disable monthly plans at or below current tier when user is on a yearly plan
              const isMonthlyWhenOnYearly = currentPlanType === 'YEARLY' && plan.billingCycle === 'MONTHLY';
              // Free Trial is a one-time offer — disable it if user has ever had any plan (current or past)
              // Also checks subscriptionStatus for expired/completed plans where plan_name may be empty
              const isNewUser = !cognitoIsSubscribed && !cognitoIsTrialUser;
              const hasSubscriptionHistory = isNewUser ? false : (currentPlanName !== '' || subscriptionStatus !== '');
              const isFreeTrialLocked = planNameLower === 'free trial' && hasSubscriptionHistory;
              
              const isDisabled = isCurrentPlan || isDowngrade || isMonthlyWhenOnYearly || isFreeTrialLocked;

              
              let buttonLabel = plan.buttonText;
              if (isCurrentPlan) {
                buttonLabel = 'Current Plan';
              } else if (isFreeTrialLocked) {
                buttonLabel = 'Not Available';
              }
              
              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isHighlighted={hoveredPlan === index}
                  onMouseEnter={() => setHoveredPlan(index)}
                  onMouseLeave={() => setHoveredPlan(professionalIndex !== -1 ? professionalIndex : 0)}
                  onSubscribe={handleSubscribe}
                  buttonLabel={buttonLabel}
                  disabled={isDisabled}
                  isActivatingTrial={isActivatingTrial}
                />
              );
            })}
            {/* filteredPlans are shown based on user's current plan */}
          </div>
        </div>
        <ContactSalesModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} planName="Enterprise" />
      </main>
    </div>
  );
};

export default PlansPage;